<script setup>
import { storeToRefs } from 'pinia'
import PresentLevelsCard from '@/library/PresentLevelsCard.vue'
import StrategyCard from '@/library/StrategyCard.vue'
import GoalCard from '@/library/GoalCard.vue'
import ResourceCard from '@/resources/ResourceCard.vue'
import { ref } from 'vue'
import { useLibraryStore } from '@/library/library.store.ts'
const libraryStore = useLibraryStore()
const { loadingResources, currentFolder } = storeToRefs(libraryStore)

// libraryStore.fetchResourcesForFolder({
//   studentUUID: '81010017-535a-4795-7db8-566f2fbbe913'
// })

const presentLevelLong = ref({
  content_uuid: 'abc123',
  collection_uuid: 'def456',
  content_body: {
    student_name: 'Cleopatra',
    enrolled_grade_level: '6',
    selected_subjects: [
      'Reading',
      'Writing',
      'Math',
      'Alt Academics: Reading',
      'Alt Academics: Writing',
      'Alt Academics: Math'
    ],
    updated_at: '2024-01-01T00:00:00Z'
  }
})
const presentLevelShort = ref({
  content_uuid: 'abc123',
  collection_uuid: 'def456',
  content_body: {
    student_name: 'Cleopatra',
    enrolled_grade_level: '6',
    selected_subjects: ['Reading', 'Writing', 'Math'],
    updated_at: '2024-01-01T00:00:00Z'
  }
})
const strategy = ref({
  uuid: '340330a8-0e0c-4837-7a90-74b884bcb4e5',
  collection_uuid: '11e2c7e7-30af-4b30-453e-c91433540637',
  content_type: 'saved_strategy',
  content_uuid: 'c259d7dc-1bf2-4a0d-941e-2ac5f9011416',
  content_title: '',
  content_body: {
    shared: false,
    source_strategy: {
      description:
        'Manipulatives are physical objects that aid understanding of mathematical concepts or processes by allowing students to physically explore or demonstrate the concept or process. The use of manipulatives provides a way for students to learn concepts in a developmentally-appropriate, hands-on, experiential way. Mathematical manipulatives are typically used in the first step of introducing new mathematical concepts.',
      image_url:
        'https://d14w9elxmqt4gn.cloudfront.net/adaptations/math-manipulatives/2016-08-02_06:15-mathmanipulatives.JPG',
      resource_count: 10,
      title: 'Math Manipulatives',
      updated_at: '2018-10-06 02:41:10Z',
      uuid: '2ca3252b-0b29-4c97-9a25-3567cf2652ab'
    },
    strategy_uuid: '2ca3252b-0b29-4c97-9a25-3567cf2652ab',
    updated_at: '2022-09-02 22:36:10Z',
    uuid: 'c259d7dc-1bf2-4a0d-941e-2ac5f9011416'
  },
  updated_at: '2022-09-02T22:36:10.812Z'
})

const goal = ref({
  uuid: 'e9c9803b-6655-47bf-5d49-6eb508a4d6b8',
  collection_uuid: '11e2c7e7-30af-4b30-453e-c91433540637',
  content_type: 'saved_super_page',
  content_uuid: '39b36820-cbfe-4305-bb90-a0ae70f10504',
  content_title: '',
  content_thumbnail_url: '',
  content_body: {
    _id: '63128184924c2900c75c5782',
    _type: 'Goalbook::Bank::SavedGoal',
    access_grade_level_uuid: '5815c446-0b1d-4c6f-ac57-fcab21878b7a',
    access_point_level: 'approaching',
    created_at: '2022-09-02 22:19:48Z',
    goal_uuid: '34271512-3648-43a4-9a20-10b5ca612745',
    grade_level_goal:
      'By (date), given two 2-digit numbers with a sum within 100 and [[ Base Ten Blocks | base ten models ]], (name) will write the sum by using a place-value strategy (e.g., adding ones to ones and tens to tens, bundling to make ten, ten and some more), with [[ Guiding Questions | guiding questions ]] (e.g., “How many ones does each number have? What should we do when we have ten ones?”), for (4 out of 5) problems, on (3 out of 4) progress monitoring assessments.',
    grades: ['2'],
    levels_personalized: {
      grade_level: true,
      intense: false,
      mild: false,
      moderate: false
    },
    personalized: null,
    save_by_copy: null,
    share_link:
      'https://goalbook.com/toolkit/v/library/anchor-page/share/39b36820-cbfe-4305-bb90-a0ae70f10504',
    shared: true,
    standard_set_refs: [
      {
        standard_long_ref: 'CCSS.Math.Content.2.NBT.B.5',
        standard_set_code: 'CCSS'
      },
      {
        standard_long_ref: 'FL.MA.2.NSO.2.3',
        standard_set_code: 'FL'
      },
      {
        standard_long_ref: 'KY.2.NBT.5',
        standard_set_code: 'KY'
      }
    ],
    standard_sets: ['CCSS', 'FL', 'KY'],
    student_name: 'Linus',
    student_uuid: '11e2c7e7-30af-4b30-453e-c91433540637',
    subject: 'Math',
    subject_alignments: [
      {
        domains: ['Number Operations'],
        subject: 'Math'
      }
    ],
    subject_domains: ['Number Operations'],
    super_page: true,
    tags: [],
    title: 'Add & Subtract Two 2-Digit Numbers',
    updated_at: '2022-09-02 22:20:09Z',
    uuid: '39b36820-cbfe-4305-bb90-a0ae70f10504'
  },
  updated_at: '2022-09-02T22:19:47.966Z',
  sequence: 1
})

const legacyGoal = ref({
  uuid: 'db3d153c-b360-4bb6-4856-60d0d29594c1',
  collection_uuid: '11e2c7e7-30af-4b30-453e-c91433540637',
  content_type: 'saved_goal',
  content_uuid: '1cae34ba-849c-494d-9ab4-4c736546d612',
  content_title: '',
  content_thumbnail_url: '',
  content_body: {
    _id: '646d72524c5d2d00b59afb26',
    _type: 'Goalbook::Bank::SavedAltAssmtGoal',
    created_at: '2023-05-24 02:11:30Z',
    goal_uuid: 'dd9f6bd0-9ab2-4744-a9fc-ba547c00a4d8',
    grade_level_goal:
      'By (date), after reviewing a location specific weather forecast and referring to a personal [[Schedules | daily schedule]] of anticipated activities for the next day, (name) will select appropriate clothing from [[ Picture Choices | pictures of personal clothing options ]] related to both the weather (e.g. cold, hot, rainy) and activity (e.g. job, typical school day, interview) and arrive at school the next morning appropriately dressed for (4 out of 5) clothing choice opportunities.',
    grades: ['9', '10', '11', '12'],
    levels_personalized: {
      grade_level: false,
      intense: false,
      mild: false,
      moderate: false
    },
    shared: false,
    standard_set_refs: [
      {
        standard_long_ref: 'TEKS.PE.9-12.AOE.4.E',
        standard_set_code: 'TEKS.PE'
      },
      {
        standard_long_ref: 'WCSE.EERI.9-12.3',
        standard_set_code: 'WCSE'
      },
      {
        standard_long_ref: 'COACH.K-12.PM.31',
        standard_set_code: 'COACH'
      }
    ],
    standard_sets: ['TEKS.PE', 'WCSE', 'COACH', 'universal'],
    student_name: 'Linus',
    student_uuid: '11e2c7e7-30af-4b30-453e-c91433540637',
    subject: 'Alt Academics',
    subject_alignments: [
      {
        domains: ['Self Care'],
        subject: 'Alt Academics'
      }
    ],
    subject_domains: ['Self Care'],
    super_page: false,
    tags: [],
    title: 'Select Appropriate Clothing for Weather',
    updated_at: '2023-05-24 02:11:30Z',
    updated_time: 1684894290889,
    user_content_profile_id: 'common-core',
    user_uuid: '6a57c1e3-a512-4d03-43ec-84cb13120295',
    uuid: '1cae34ba-849c-494d-9ab4-4c736546d612'
  },
  contributor: '6a57c1e3-a512-4d03-43ec-84cb13120295',
  updated_at: '2023-05-24T02:11:30.883Z'
})

const resourceEnglish = ref({
  description:
    'Collection of assessments to be used for progress monitoring of the annual target. These resources are available both as PDFs and Google Documents. Answer keys included.',
  download_links: [
    {
      label: 'Assessment Collection (PDF)',
      url: 'https://s3.amazonaws.com/atlas-production.goalbookapp.com/resource-ae50596f-d8a0-45f6-401c-fc65e706d87f/Progress+Monitoring+Assessments_+Add+within+100+with+Models.pdf'
    }
  ],
  google_drive_links: [
    {
      label: 'Assessment Collection',
      link_type: 'document',
      url: 'https://docs.google.com/document/d/1qcLQTBn9IEu8X6xQK5GmrbCbQz4GxnlFfd0kBW7ONJY/edit?usp=sharing'
    }
  ],
  grades: ['2'],
  language: 'english',
  num_pages: 8,
  screenshots: [
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-001.png',
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-002.png',
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-003.png',
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-004.png',
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-005.png'
  ],
  subjects: ['Math'],
  thumbnail_url:
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-001.png',
  title: 'Progress Monitoring Assessments: Add within 100 with Models',
  type: 'Assessment Collection',
  uuid: 'ae50596f-d8a0-45f6-401c-fc65e706d87f',
  collectionUuid: '11e2c7e7-30af-4b30-453e-c91433540637'
})
const resourceSpanish = ref({
  description:
    'Recopilación de evaluaciones que se utilizarán para el monitoreo del progreso del objetivo anual. Estos recursos están disponibles tanto en PDF como en Google Documents. Se incluyen las claves de respuesta.',
  download_links: [
    {
      label:
        'Evaluaciones del monitoreo de progreso: Determinar el tema en los textos literarios (PDF)',
      url: 'https://s3.amazonaws.com/atlas-production.goalbookapp.com/resource-7015283a-9669-4b3d-5e6c-e56bc50d7ef4/Evaluaciones+del+monitoreo+de+progreso_+Determinar+el+tema+en+los+textos+literarios.pdf'
    }
  ],
  google_drive_links: [
    {
      label: 'Evaluaciones del monitoreo de progreso: Determinar el tema en los textos literarios',
      link_type: 'document',
      url: 'https://docs.google.com/document/d/1DwCtsmtsJrq0BfwHdibgmC5wQBW4sKwf9S2wKrdbzMc/edit#heading=h.mis6ag5p353w'
    }
  ],
  grades: ['4'],
  language: 'spanish',
  num_pages: 16,
  screenshots: [
    'https://assets.goalbookapp.com/screenshots/7015283a-9669-4b3d-5e6c-e56bc50d7ef4/1693417946884-Evaluaciones_del_monitoreo_de_progreso__Determinar_el_tema_en_los_textos_literarios-001.png',
    'https://assets.goalbookapp.com/screenshots/7015283a-9669-4b3d-5e6c-e56bc50d7ef4/1693417946884-Evaluaciones_del_monitoreo_de_progreso__Determinar_el_tema_en_los_textos_literarios-002.png',
    'https://assets.goalbookapp.com/screenshots/7015283a-9669-4b3d-5e6c-e56bc50d7ef4/1693417946884-Evaluaciones_del_monitoreo_de_progreso__Determinar_el_tema_en_los_textos_literarios-003.png',
    'https://assets.goalbookapp.com/screenshots/7015283a-9669-4b3d-5e6c-e56bc50d7ef4/1693417946884-Evaluaciones_del_monitoreo_de_progreso__Determinar_el_tema_en_los_textos_literarios-005.png'
  ],
  subjects: ['Reading'],
  thumbnail_url:
    'https://assets.goalbookapp.com/screenshots/7015283a-9669-4b3d-5e6c-e56bc50d7ef4/1693417946884-Evaluaciones_del_monitoreo_de_progreso__Determinar_el_tema_en_los_textos_literarios-001.png',
  title: 'Evaluaciones del monitoreo de progreso: Determinar el tema en los textos literarios',
  type: 'Recopilación de evaluaciones',
  uuid: '7015283a-9669-4b3d-5e6c-e56bc50d7ef4',
  collectionUuid: '11e2c7e7-30af-4b30-453e-c91433540637'
})

const resourceToSave = ref({
  description:
    'Collection of assessments to be used for progress monitoring of the annual target. These resources are available both as PDFs and Google Documents. Answer keys included.',
  download_links: [
    {
      label: 'Assessment Collection (PDF)',
      url: 'https://s3.amazonaws.com/atlas-production.goalbookapp.com/resource-ae50596f-d8a0-45f6-401c-fc65e706d87f/Progress+Monitoring+Assessments_+Add+within+100+with+Models.pdf'
    }
  ],
  google_drive_links: [
    {
      label: 'Assessment Collection',
      link_type: 'document',
      url: 'https://docs.google.com/document/d/1qcLQTBn9IEu8X6xQK5GmrbCbQz4GxnlFfd0kBW7ONJY/edit?usp=sharing'
    }
  ],
  grades: ['2'],
  language: 'english',
  num_pages: 8,
  screenshots: [
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-001.png',
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-002.png',
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-003.png',
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-004.png',
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-005.png'
  ],
  subjects: ['Math'],
  thumbnail_url:
    'https://assets.goalbookapp.com/screenshots/ae50596f-d8a0-45f6-401c-fc65e706d87f/1638990587576-Progress_Monitoring_Assessments__Add_within_100_with_Models-001.png',
  title: 'Progress Monitoring Assessments: Add within 100 with Models',
  type: 'Assessment Collection',
  uuid: 'ae50596f-d8a0-45f6-401c-fc65e706d87f'
})
</script>

<template>
  <h3 class="header-md p-3">Present Levels</h3>
  <div class="columns-1 md:columns-3">
    <PresentLevelsCard :presentLevel="presentLevelShort" />
    <PresentLevelsCard :presentLevel="presentLevelLong" />
    <PresentLevelsCard :presentLevel="presentLevelLong" />
  </div>
  <h3 class="header-md p-3">Strategies</h3>
  <div class="columns-1 md:columns-2">
    <StrategyCard :strategy="strategy" />
    <StrategyCard :strategy="strategy" />
  </div>
  <h3 class="header-md p-3">Goals</h3>
  <div class="columns-1 md:columns-3">
    <GoalCard :goal="goal" />
    <GoalCard :goal="goal" />
    <GoalCard :goal="legacyGoal" />
  </div>
  <h3 class="header-md p-3">Resources</h3>
  <ResourceCard v-bind="resourceEnglish" />
  <ResourceCard v-bind="resourceSpanish" />
  <ResourceCard v-bind="resourceToSave" />

  <div v-if="loadingResources">Loading...</div>
  <div v-else>{{ currentFolder.name }}</div>
</template>
